import type { AppResource, InventoryCell, InventoryListing, ListingSnapshotResource } from '@studio/shared';
import { useMemo, type ReactNode } from 'react';
import { api } from '../shell/api.js';
import { Failure } from '../shell/EmptyState.js';
import { useAsync } from '../shell/useAsync.js';
import {
  ASSET_LABEL,
  compareReadings,
  countryName,
  languageName,
  machineTranslationOf,
  readingWhen,
  storeListingLabel,
  type ComparedGraphic,
  type ComparedText,
} from './model.js';

/**
 * Two readings of one Market Locale, side by side.
 *
 * The comparison opens the two readings by their ids - the one the cell holds and the
 * one before it - and never the list of every reading of the Store Listing: two
 * readings are all a comparison is. A reading the operator corrected is shown as
 * corrected; the snapshot it corrects is opened by its own id only for the day the
 * storefront was read. What was not observed is said in words: an absent reading is not
 * an empty value, and a part that was never read is not a deletion.
 */

type SeenMark = Exclude<ComparedGraphic['mark'], 'unobserved'>;

/** What became of one graphic between the two readings, in the gender of its name. */
const GRAPHIC_MARK: Record<SeenMark, string> = {
  same: 'тот же',
  added: 'добавлен',
  removed: 'убран',
  moved: 'перемещён',
};
const ICON_MARK: Record<SeenMark, string> = {
  same: 'та же',
  added: 'добавлена',
  removed: 'убрана',
  moved: 'перемещена',
};

function graphicLine(graphic: ComparedGraphic, side: 'before' | 'after'): string {
  const name = `${graphic.position}. ${ASSET_LABEL[graphic.role]}`;
  // Only this reading could see it: the other one says nothing about it either way.
  if (graphic.mark === 'unobserved') return `${name} — не наблюдалось в ${side === 'after' ? 'прошлом' : 'текущем'} чтении`;
  // The other place is said only for a graphic that moved: one that kept its order has
  // nothing to say about it. Seen from the reading before, that place is where it went.
  const place =
    graphic.mark === 'moved' && graphic.other !== null
      ? `, ${side === 'after' ? 'прежняя' : 'теперь'} позиция ${graphic.other}`
      : '';
  return `${name} — ${(graphic.role === 'appIcon' ? ICON_MARK : GRAPHIC_MARK)[graphic.mark]}${place}`;
}

/** What one reading could not observe of its graphics, said under its own list. */
function Unobserved({ roles }: { roles: readonly ComparedGraphic['role'][] }): ReactNode {
  if (roles.length === 0) return null;
  return <p className="note">{`${roles.map((role) => ASSET_LABEL[role]).join(', ')} не наблюдалось: страница прочитана из разметки.`}</p>;
}

/** What became of one text, said only of what both readings observed. */
function textMark(text: ComparedText): string {
  if (text.change === 'changed') return 'изменилось';
  if (text.change === 'unchanged') return 'не изменилось';
  if (text.before === null && text.after === null) return 'не наблюдалось ни в одном чтении';
  return text.before === null ? 'не наблюдалось в прошлом чтении' : 'не наблюдалось в текущем чтении';
}

/** One side of the comparison: what the reading says now, and when the storefront was read. */
interface Reading {
  readonly snapshot: ListingSnapshotResource;
  /** Null when the reading a correction corrects could not be read: the day is then unknown. */
  readonly readAt: string | null;
}

/**
 * A reading by its id. The operator's correction of a reading is a snapshot of its own,
 * written when it was made; the storefront was read when the reading it corrects - or the
 * one that one corrects - was, and that is the day the reading keeps.
 */
async function readingOf(id: string): Promise<Reading> {
  const snapshot = await api.readSnapshot(id);
  return { snapshot, readAt: await storefrontReadAt(snapshot) };
}

/**
 * The day the storefront was read, found down the chain of corrections. A link that
 * cannot be read leaves that day unknown, never the reading itself unread: what the
 * reading says has already arrived.
 */
async function storefrontReadAt(snapshot: ListingSnapshotResource): Promise<string | null> {
  let read = snapshot;
  try {
    while (read.supersedesId !== null) read = await api.readSnapshot(read.supersedesId);
  } catch {
    return null;
  }
  return read.capturedAt;
}

/** When a reading was read, and corrected, or what is known about it when it was not read at all. */
function when(reading: Reading | null, loading: boolean, error: string | null): string {
  if (reading !== null) return readingWhen(reading.readAt, reading.snapshot.reviewedAt);
  if (error !== null) return 'не прочитано';
  return loading ? 'читаю…' : 'не прочитано';
}

/** What was observed of the machine translation of one reading, and what was not. */
function translation(snapshot: ListingSnapshotResource | null, loading: boolean): string {
  if (snapshot === null) return loading ? 'читаю…' : 'не наблюдалось';
  const observed = machineTranslationOf(snapshot);
  if (observed === null) return 'не наблюдалось: страница прочитана из разметки';
  return observed ? 'Google Play перевёл сам' : 'машинного перевода нет';
}

export function Comparison({
  app,
  listing,
  cell,
  onClose,
}: {
  app: AppResource;
  listing: InventoryListing;
  cell: InventoryCell;
  onClose: () => void;
}): ReactNode {
  const current = useAsync<Reading | null>(
    async () => (cell.snapshotId === null ? null : readingOf(cell.snapshotId)),
    [cell.snapshotId],
  );
  const previous = useAsync<Reading | null>(
    async () => (cell.previousSnapshotId === null ? null : readingOf(cell.previousSnapshotId)),
    [cell.previousSnapshotId],
  );
  const now = current.value?.snapshot ?? null;
  const before = previous.value?.snapshot ?? null;
  const compared = useMemo(
    () => (now !== null && before !== null ? compareReadings(before, now) : null),
    [before, now],
  );
  // There is no reading before this one: the comparison says so, and shows the one there is.
  const noPrevious = cell.previousSnapshotId === null;

  return (
    <section
      className="snapshot-view"
      aria-label={`Сравнение чтений · ${countryName(cell.market)} · ${languageName(cell.locale)}`}
    >
      <div className="inventory-listing-head">
        <p className="eyebrow">{`Сравнение · ${countryName(cell.market)} · ${languageName(cell.locale)}`}</p>
        <button type="button" className="ghost" onClick={onClose}>
          Закрыть
        </button>
      </div>
      <p className="note">
        {`${app.displayName} · ${storeListingLabel(listing.storeListing)} · ${countryName(cell.market)} (${cell.market}) · ${languageName(cell.locale)} (${cell.locale})`}
      </p>
      {current.error === null ? null : <Failure message={current.error} />}
      {previous.error === null ? null : <Failure message={previous.error} />}

      <dl className="facts comparison-facts">
        <div>
          <dt>Текущее чтение</dt>
          <dd>{when(current.value, current.loading, current.error)}</dd>
        </div>
        <div>
          <dt>Предыдущее чтение</dt>
          <dd>
            {noPrevious
              ? 'предыдущего чтения нет: эту Market Locale прочитали впервые'
              : when(previous.value, previous.loading, previous.error)}
          </dd>
        </div>
        <div>
          <dt>Машинный перевод в прошлом чтении</dt>
          <dd>{translation(before, previous.loading)}</dd>
        </div>
        <div>
          <dt>Машинный перевод в текущем чтении</dt>
          <dd>{translation(now, current.loading)}</dd>
        </div>
      </dl>

      {compared === null ? null : (
        <>
          {/* On a wide screen a word wider than its column - a long address - scrolls the table inside the card, never the page. */}
          <div className="comparison-texts-scroll">
            <table className="country-table comparison-texts">
              <thead>
                <tr>
                  <th scope="col">Что</th>
                  <th scope="col">Прошлое</th>
                  <th scope="col">Текущее</th>
                </tr>
              </thead>
              <tbody>
                {compared.texts.map((text) => (
                  <tr key={text.label}>
                    <th scope="row">{`${text.label}: ${textMark(text)}`}</th>
                    <td>{text.before ?? 'не прочитано'}</td>
                    <td>{text.after ?? 'не прочитано'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h4>Графика</h4>
          <p className="note">Состав и порядок показаны по позициям.</p>
          <h5>Предыдущее чтение</h5>
          <Unobserved roles={compared.graphics.unobserved.before} />
          {compared.graphics.before.length === 0 ? (
            <p className="note">В прошлом чтении графики не показаны.</p>
          ) : (
            <ul aria-label="Графика в прошлом чтении">
              {compared.graphics.before.map((graphic) => (
                <li key={`before-${graphic.position}-${graphic.role}`}>{graphicLine(graphic, 'before')}</li>
              ))}
            </ul>
          )}
          <h5>Текущее чтение</h5>
          <Unobserved roles={compared.graphics.unobserved.after} />
          {compared.graphics.after.length === 0 ? (
            <p className="note">В текущем чтении графики не показаны.</p>
          ) : (
            <ul aria-label="Графика в текущем чтении">
              {compared.graphics.after.map((graphic) => (
                <li key={`after-${graphic.position}-${graphic.role}`}>{graphicLine(graphic, 'after')}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
